import type { LaunchDetail, LaunchStatus, NextAction } from './types';

function getCheckoutBaseEligibility(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  if (detail.paid_enrollment_enabled) {
    return { allowed: false, reason: 'Paid enrollment already active' };
  }
  if (detail.billing_interval === 'yearly' && detail.offered_billing_agreement?.collection_ready !== true) {
    return { allowed: false, reason: 'Annual payment collection is not ready' };
  }
  if (detail.offered_billing_agreement === null || detail.offered_billing_agreement?.collection_ready === false) {
    return { allowed: false, reason: 'No payment offer is available' };
  }
  if (!detail.contact_email) {
    return { allowed: false, reason: 'No contact email configured' };
  }
  if (!detail.demo) {
    return { allowed: false, reason: 'Demo is not enabled' };
  }
  if (isDemoAccessDisabled(detail.demo)) {
    return { allowed: false, reason: 'Demo access is disabled' };
  }
  return { allowed: true };
}

export function isDemoAccessDisabled(demo: NonNullable<LaunchDetail['demo']>): boolean {
  return Boolean(demo.access_disabled || demo.access_code_disabled_at);
}

function hasCheckoutAttemptHistory(detail: LaunchDetail): boolean {
  return Array.isArray(detail.checkout_attempts) && detail.checkout_attempts.length > 0;
}

export function canCreateInitialCheckout(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  // Priority order matches the plan: paid enrollment, existing checkout/history,
  // missing contact email, disabled/missing demo, then backend next_action.
  const baseEligibility = getCheckoutBaseEligibility(detail);
  if (!baseEligibility.allowed) {
    return baseEligibility;
  }
  if (detail.checkout) {
    return { allowed: false, reason: 'Checkout already created' };
  }
  if (hasCheckoutAttemptHistory(detail)) {
    return { allowed: false, reason: 'Payment link history already exists' };
  }
  // Payment-link creation is a parallel operator action once a demo exists,
  // not strictly the single backend "next action". Allow both send_demo
  // (fresh demo-only launch) and open_checkout (backend advanced state).
  if (detail.next_action !== 'open_checkout' && detail.next_action !== 'send_demo') {
    return {
      allowed: false,
      reason: `Current action: ${nextActionLabel(detail.next_action)}`,
    };
  }
  return { allowed: true };
}

export function canRegenerateCheckout(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  const baseEligibility = getCheckoutBaseEligibility(detail);
  if (!baseEligibility.allowed) {
    return baseEligibility;
  }
  if (!detail.checkout && !hasCheckoutAttemptHistory(detail)) {
    return { allowed: false, reason: 'No payment link history exists yet' };
  }
  const attempts = [detail.checkout, ...(detail.checkout_attempts ?? [])];
  if (attempts.some((attempt) => attempt?.status === 'pending' || attempt?.status === 'unknown')) {
    return { allowed: false, reason: 'Checkout outcome is not confirmed' };
  }
  if (
    detail.next_action !== 'open_checkout' &&
    detail.next_action !== 'send_demo' &&
    detail.next_action !== 'wait_for_checkout'
  ) {
    return {
      allowed: false,
      reason: `Current action: ${nextActionLabel(detail.next_action)}`,
    };
  }
  return { allowed: true };
}

export function canCreateCheckout(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  return canCreateInitialCheckout(detail);
}

export function nextActionLabel(action: NextAction): string {
  switch (action) {
    case 'send_demo':
      return 'Send demo invite';
    case 'open_checkout':
      return 'Generate payment link';
    case 'wait_for_checkout':
      return 'Awaiting clinic payment';
    case 'wait_for_subscription_webhook':
      return 'Confirming subscription';
    case 'approve_launch':
      return 'Ready for internal approval';
    case 'paid_active':
      return 'Paid enrollment active';
    case 'operator_recovery':
      return 'Operator recovery';
    default:
      return 'Unknown status';
  }
}

export function launchStatusLabel(status: LaunchStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'checkout_pending':
      return 'Checkout pending';
    case 'payment_recorded':
      return 'Payment recorded';
    case 'paid_active':
      return 'Paid active';
    case 'paid_paused':
      return 'Paid paused';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
}

export function canApproveLaunch(detail: LaunchDetail): boolean {
  return detail.next_action === 'approve_launch' && !detail.demo;
}

export function isPaidActive(_nextAction: NextAction, paidEnrollmentEnabled: boolean): boolean {
  // Backend active signal is the authority; paid_enrollment_enabled being true
  // is enough regardless of the current next_action label.
  return paidEnrollmentEnabled;
}

export function subscriptionLabel(subscriptionStatus?: string): string {
  switch (subscriptionStatus) {
    case 'active':
      return 'Active';
    case 'pending':
      return 'Pending';
    case 'cancelled':
      return 'Cancelled';
    case 'past_due':
      return 'Past due';
    case 'on_hold':
      return 'On hold';
    case 'expired':
      return 'Expired';
    case 'paused':
      return 'Paused';
    case 'failed':
      return 'Failed';
    case 'missing':
      return 'Not created';
    default:
      return 'Unknown';
  }
}

export function paymentLabel(paymentStatus?: string): string {
  switch (paymentStatus) {
    case 'succeeded':
      return 'Payment received';
    case 'pending':
      return 'Payment pending';
    case 'failed':
      return 'Payment failed';
    case 'unknown':
      return 'No payment evidence';
    default:
      return 'Unknown';
  }
}

/**
 * Returns true when a demo access-code lockout is currently active. Durable
 * detail may carry a `locked_until` timestamp from a past lockout that has
 * since expired, so this must compare against the current time rather than
 * treating any set value as an active lock.
 */
export function isDemoAccessLocked(lockedUntil?: string, now: Date = new Date()): boolean {
  if (!lockedUntil) {
    return false;
  }
  const lockedUntilMs = Date.parse(lockedUntil);
  if (Number.isNaN(lockedUntilMs)) {
    return false;
  }
  return lockedUntilMs > now.getTime();
}

/** Label for demo access-code enabled/disabled state, derived from the shared disabled signal. */
export function demoAccessCodeStateLabel(demo: NonNullable<LaunchDetail['demo']>): string {
  return isDemoAccessDisabled(demo) ? 'Disabled' : 'Enabled';
}

/**
 * Rotation is available when the launch has a demo and the access code is
 * not currently disabled. `access_disabled` and `access_code_disabled_at`
 * both represent disabled access, depending on backend generation.
 */
export function canRotateAccessCode(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  if (!detail.demo) {
    return { allowed: false, reason: 'Demo is not enabled' };
  }
  if (isDemoAccessDisabled(detail.demo)) {
    return { allowed: false, reason: 'Access code is disabled' };
  }
  return { allowed: true };
}

/**
 * Returns true when the demo's `expires_at` timestamp is in the past.
 * Durable detail may carry an `expires_at` that has not yet passed, so this
 * must compare against the current time rather than treating any set value
 * as expired (mirrors `isDemoAccessLocked`'s convention for `locked_until`).
 * Used to gate enable-access-code eligibility (U5 TS5): an expired demo
 * should not be recoverable via enable even if access is currently disabled.
 */
export function isDemoExpired(expiresAt?: string, now: Date = new Date()): boolean {
  if (!expiresAt) {
    return false;
  }
  const expiresAtMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return false;
  }
  return expiresAtMs <= now.getTime();
}

/**
 * Disable is available whenever the launch has a demo and the access code is
 * not already disabled (KTD4: backend state, not status, is authoritative).
 */
export function canDisableAccessCode(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  if (!detail.demo) {
    return { allowed: false, reason: 'Demo is not enabled' };
  }
  if (isDemoAccessDisabled(detail.demo)) {
    return { allowed: false, reason: 'Access code is already disabled' };
  }
  return { allowed: true };
}

/**
 * Enable is an admin recovery/state action (R9): it is only offered when
 * access is currently disabled AND the demo is not expired or paid-converted.
 * Reusing `isPaidActive` keeps the paid-conversion signal consistent with the
 * rest of this module rather than re-deriving it from `detail.status`.
 */
export function canEnableAccessCode(detail: LaunchDetail): { allowed: boolean; reason?: string } {
  if (!detail.demo) {
    return { allowed: false, reason: 'Demo is not enabled' };
  }
  if (!isDemoAccessDisabled(detail.demo)) {
    return { allowed: false, reason: 'Access code is not disabled' };
  }
  if (isDemoExpired(detail.demo.expires_at)) {
    return { allowed: false, reason: 'Demo has expired' };
  }
  if (isPaidActive(detail.next_action, detail.paid_enrollment_enabled)) {
    return { allowed: false, reason: 'Launch has converted to paid' };
  }
  return { allowed: true };
}

/**
 * Returns true when durable demo detail carries any stable-URL or
 * access-code metadata worth showing in a dedicated "Demo access" section.
 * Demos that predate this feature (or launches without a demo at all)
 * return false so the section is skipped entirely.
 */
export function hasDemoAccessMetadata(demo?: LaunchDetail['demo']): boolean {
  if (!demo) {
    return false;
  }
  return (
    demo.stable_demo_url !== undefined ||
    demo.access_disabled === true ||
    demo.access_code_rotated_at !== undefined ||
    demo.access_code_disabled_at !== undefined ||
    demo.locked_until !== undefined ||
    demo.failed_login_count !== undefined
  );
}

export function billingPlanLabel(interval: string): string {
  switch (interval) {
    case 'monthly': return 'Monthly';
    case 'yearly': return 'Annual';
    default: return 'Unknown plan';
  }
}
